/**
 * Infinite Loop Recovery UI Component
 * User-friendly interface for recovering from infinite loops and React error #185
 * Provides clear guidance and recovery options for users
 */

import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Typography,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  BugReport as BugIcon,
  AutoFixHigh as AutoFixIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface InfiniteLoopRecoveryProps {
  error?: Error;
  recoveryAttempts: number;
  onRecover: () => void;
  onForceReload: () => void;
  isGeneralError?: boolean;
}

export const InfiniteLoopRecovery: React.FC<InfiniteLoopRecoveryProps> = ({
  error,
  recoveryAttempts,
  onRecover,
  onForceReload,
  isGeneralError = false
}) => {
  const navigate = useNavigate();
  const [autoRecovering, setAutoRecovering] = React.useState(false);

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleAutoRecover = async () => {
    setAutoRecovering(true);
    
    // Wait a moment to show loading state
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setAutoRecovering(false);
    onRecover();
  };

  const getErrorSeverity = () => {
    if (recoveryAttempts >= 3) return 'error';
    if (recoveryAttempts >= 1) return 'warning';
    return 'info';
  };

  const getErrorTitle = () => {
    if (isGeneralError) return 'Application Error Detected';
    if (recoveryAttempts >= 3) return 'Critical System Error';
    if (recoveryAttempts >= 1) return 'Repeated Loop Detected';
    return 'Infinite Loop Detected';
  };

  const getErrorMessage = () => {
    if (isGeneralError) {
      return 'An unexpected error occurred in the application. We can try to recover automatically.';
    }
    
    if (recoveryAttempts >= 3) {
      return 'Multiple recovery attempts failed. The system may need a full refresh to restore normal operation.';
    }
    
    if (recoveryAttempts >= 1) {
      return 'The system detected a repeated infinite loop. This may indicate a deeper issue that requires attention.';
    }
    
    return 'The system detected an infinite update loop that could cause performance issues. Recovery options are available below.';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3,
        backgroundColor: 'background.default'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '100%',
          p: 4,
          textAlign: 'center'
        }}
      >
        {/* Error Alert */}
        <Alert 
          severity={getErrorSeverity()} 
          icon={<ErrorIcon />}
          sx={{ mb: 3, textAlign: 'left' }}
        >
          <AlertTitle>{getErrorTitle()}</AlertTitle>
          {getErrorMessage()}
        </Alert>

        {/* Recovery Status */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recovery Status
          </Typography>
          <Chip
            icon={recoveryAttempts >= 3 ? <WarningIcon /> : <InfoIcon />}
            label={`Recovery Attempts: ${recoveryAttempts}`}
            color={recoveryAttempts >= 3 ? 'error' : 'info'}
            variant="outlined"
          />
        </Box>

        {/* Error Details (if available) */}
        {error && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Error Details:
            </Typography>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                backgroundColor: 'grey.50',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: 120
              }}
            >
              {error.message}
            </Paper>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Recovery Options */}
        <Typography variant="h6" gutterBottom>
          Recovery Options
        </Typography>

        <List sx={{ textAlign: 'left', mb: 3 }}>
          <ListItem>
            <ListItemIcon>
              <AutoFixIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Smart Recovery"
              secondary="Attempt automatic recovery while preserving your data"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <RefreshIcon color="warning" />
            </ListItemIcon>
            <ListItemText
              primary="Force Reload"
              secondary="Reload the entire application (may lose unsaved changes)"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <HomeIcon color="info" />
            </ListItemIcon>
            <ListItemText
              primary="Return to Dashboard"
              secondary="Navigate back to the main dashboard"
            />
          </ListItem>
        </List>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={autoRecovering ? <CircularProgress size={16} /> : <AutoFixIcon />}
            onClick={handleAutoRecover}
            disabled={autoRecovering || recoveryAttempts >= 3}
          >
            {autoRecovering ? 'Recovering...' : 'Smart Recovery'}
          </Button>

          <Button
            variant="outlined"
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={onForceReload}
          >
            Force Reload
          </Button>

          <Button
            variant="outlined"
            color="info"
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
          >
            Go to Dashboard
          </Button>
        </Box>

        {/* Additional Help */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            If this problem persists, please contact support with the error details above.
          </Typography>
        </Box>

        {/* Debug Information (Development Only) */}
        {import.meta.env.DEV && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              startIcon={<BugIcon />}
              onClick={() => console.error('Debug Error Details:', { error, recoveryAttempts })}
            >
              Log Debug Info
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default InfiniteLoopRecovery;
